import { useState, type KeyboardEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { DURATION_TYPE_OPTIONS } from "@project/types/gaushala";

export type GaushalaSchemeFormState = Record<string, unknown>;

export type GaushalaSchemeFormFieldsProps = {
  editing: GaushalaSchemeFormState | null;
  setEditing: (value: GaushalaSchemeFormState | null) => void;
};

function patchEditing(
  editing: GaushalaSchemeFormState | null,
  setEditing: GaushalaSchemeFormFieldsProps["setEditing"],
  updates: GaushalaSchemeFormState
) {
  setEditing({ ...(editing ?? {}), ...updates });
}

function getFeatures(editing: GaushalaSchemeFormState | null): string[] {
  const value = editing?.features;
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => String(item).trim()).filter(Boolean);
}

export function GaushalaSchemeFormFields({
  editing,
  setEditing,
}: GaushalaSchemeFormFieldsProps) {
  const [featureDraft, setFeatureDraft] = useState("");
  const features = getFeatures(editing);

  const addFeature = () => {
    const next = featureDraft.trim();
    if (!next) {
      return;
    }
    if (features.some(item => item.toLowerCase() === next.toLowerCase())) {
      setFeatureDraft("");
      return;
    }
    patchEditing(editing, setEditing, {
      features: [...features, next],
    });
    setFeatureDraft("");
  };

  const removeFeature = (index: number) => {
    patchEditing(editing, setEditing, {
      features: features.filter((_, i) => i !== index),
    });
  };

  const onFeatureKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFeature();
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <Label data-required="true">Title</Label>
        <Input
          value={String(editing?.title ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { title: e.target.value })
          }
          placeholder="1 Year Fodder Sponsorship"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label>Short Title</Label>
        <Input
          value={String(editing?.short_title ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { short_title: e.target.value })
          }
          placeholder="1Yr"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label>Duration Type</Label>
        <Select
          value={String(editing?.duration_type ?? "year")}
          onValueChange={v =>
            patchEditing(editing, setEditing, { duration_type: v })
          }
        >
          <SelectTrigger className="bg-muted/40 rounded-xl border-transparent">
            <SelectValue placeholder="Select duration type" />
          </SelectTrigger>
          <SelectContent>
            {DURATION_TYPE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Duration</Label>
        <Input
          type="number"
          min={1}
          value={String(editing?.duration ?? "1")}
          onChange={e =>
            patchEditing(editing, setEditing, { duration: e.target.value })
          }
          placeholder="1"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label data-required="true">Amount (₹)</Label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={String(editing?.amount ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { amount: e.target.value })
          }
          placeholder="3650.00"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label>Per Day Amount (₹)</Label>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={String(editing?.per_day_amount ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, {
              per_day_amount: e.target.value,
            })
          }
          placeholder="10.00"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Description</Label>
        <Textarea
          value={String(editing?.description ?? "")}
          onChange={e =>
            patchEditing(editing, setEditing, { description: e.target.value })
          }
          placeholder="Sponsor one cow for a full year."
          rows={3}
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div className="sm:col-span-2">
        <Label>Features</Label>
        <div className="flex gap-2">
          <Input
            value={featureDraft}
            onChange={e => setFeatureDraft(e.target.value)}
            onKeyDown={onFeatureKeyDown}
            placeholder="Add a feature and press Enter"
            className="bg-muted/40 rounded-xl border-transparent"
          />
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 gap-1 rounded-xl"
            onClick={addFeature}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
          <Table className="[&_td]:border-border/70 border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
            <TableHeader className="bg-muted/60">
              <TableRow className="hover:bg-muted/60">
                <TableHead className="w-14 px-3 py-2 text-center font-semibold text-foreground">
                  #
                </TableHead>
                <TableHead className="px-3 py-2 font-semibold text-foreground">
                  Feature
                </TableHead>
                <TableHead className="w-20 px-3 py-2 text-right font-semibold text-foreground">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    No features added yet
                  </TableCell>
                </TableRow>
              ) : (
                features.map((feature, index) => (
                  <TableRow key={`${feature}`}>
                    <TableCell className="px-3 py-2.5 text-center text-sm text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-sm text-foreground">
                      {feature}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => removeFeature(index)}
                        aria-label={`Remove feature ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <Label>Button Text</Label>
        <Input
          value={String(editing?.button_text ?? "SPONSOR NOW")}
          onChange={e =>
            patchEditing(editing, setEditing, { button_text: e.target.value })
          }
          placeholder="SPONSOR NOW"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div>
        <Label>Display Order</Label>
        <Input
          type="number"
          min={0}
          value={String(editing?.display_order ?? "1")}
          onChange={e =>
            patchEditing(editing, setEditing, {
              display_order: e.target.value,
            })
          }
          placeholder="1"
          className="bg-muted/40 rounded-xl border-transparent"
        />
      </div>

      <div className="bg-muted/40 flex items-center gap-3 rounded-xl px-3.5 py-2.5">
        <Switch
          checked={editing?.is_highlighted === true}
          onCheckedChange={v =>
            patchEditing(editing, setEditing, { is_highlighted: v })
          }
        />
        <div>
          <p className="text-sm font-medium">Highlighted</p>
          <p className="text-xs text-muted-foreground">
            {editing?.is_highlighted === true ? "Featured scheme" : "Normal"}
          </p>
        </div>
      </div>

      <div className="bg-muted/40 flex items-center gap-3 rounded-xl px-3.5 py-2.5">
        <Switch
          checked={editing?.is_active !== false}
          onCheckedChange={v =>
            patchEditing(editing, setEditing, { is_active: v })
          }
        />
        <div>
          <p className="text-sm font-medium">Active</p>
          <p className="text-xs text-muted-foreground">
            {editing?.is_active !== false ? "Visible to public" : "Inactive"}
          </p>
        </div>
      </div>
    </div>
  );
}
